"use client";

// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { RootState } from "@/store/store";
import { useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import {
  LEAD_DROP_FORM_SECTION_VARIANTS,
  LEAD_DROP_FORM_VARIANTS,
  PAGE_TYPES,
} from "../../../constants";

/**
 * Blog with lead: article left / full lead form right on desktop; stacked on mobile.
 * When `formColumnOnly`, renders only the lead form (used inside blog page band; rich text comes from separate RTE sections).
 */
export default function BlogWithLead1({
  data,
  formColumnOnly = false,
}: {
  data: any;
  formColumnOnly?: boolean;
}) {
  /** Public API sends the row on `component.data`; admin preview may nest `{ data: row }`. */
  const sectionData = data?.data ?? data;
  const widgetRef = useRef<HTMLElement | null>(null);

  const leaddropFormVariant = useSelector(
    (state: RootState) => state.leaddropform.variant,
  );
  const leadFormStore = useSelector(
    (state: RootState) => state.leaddropform.lead_drop_form,
  ) as { formData?: unknown } | null;

  const pageCategory = useSelector(
    (state: RootState) => state.globalslice.pageCategory,
  );
  const isBlogPage =
    String(pageCategory ?? "").toLowerCase() === PAGE_TYPES.BLOG.toLowerCase();

  const resolvedLeadFormVariant = useMemo(() => {
    if (leaddropFormVariant != null && String(leaddropFormVariant).trim() !== "") {
      return String(leaddropFormVariant);
    }
    if (leadFormStore?.formData) {
      return LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_1;
    }
    return null;
  }, [leaddropFormVariant, leadFormStore]);

  const LeaddropFormComponent = useMemo(() => {
    if (!resolvedLeadFormVariant) return null;
    return LEAD_DROP_FORM_SECTION_VARIANTS[resolvedLeadFormVariant] ?? null;
  }, [resolvedLeadFormVariant]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (widgetRef.current) {
  //     FireWidgetViewedEvent(widgetRef, "blog_with_lead_section");
  //   }
  // }, []);

  if (!sectionData) {
    return null;
  }

  const isActive =
    sectionData?.status === true ||
    sectionData?.status === 1 ||
    sectionData?.status === "1";

  if (!isActive) {
    return null;
  }

  const enablePadding =
    sectionData?.enable_padding === true ||
    sectionData?.enable_padding === 1 ||
    sectionData?.enable_padding === "1";

  const enableLeadForm =
    sectionData?.enable_lead_form === true ||
    sectionData?.enable_lead_form === 1 ||
    sectionData?.enable_lead_form === "1";

  const articleBand = String(
    sectionData?.article_band_background_color ?? "",
  ).trim();
  const backgroundColor =
    articleBand || sectionData?.background_color || "#FFFFFF";

  const showLead =
    Boolean(enableLeadForm) &&
    Boolean(resolvedLeadFormVariant) &&
    Boolean(LeaddropFormComponent);

  let leadFormAside: React.ReactNode = null;
  if (showLead && LeaddropFormComponent) {
    const LeadForm = LeaddropFormComponent;
    leadFormAside = (
      <aside
        className={cn(
          "order-2 flex w-full min-w-0 shrink-0 flex-col items-center sm:items-stretch",
          isBlogPage && "max-w-full",
          "sm:w-[440px] sm:self-start sm:sticky sm:top-24",
          "md:w-[450px]",
          "lg:w-[475px]",
          "xl:w-[560px]",
        )}
      >
        <LeadForm hideTermsCheckbox />
      </aside>
    );
  }

  if (formColumnOnly) {
    if (!showLead || !LeaddropFormComponent) {
      return null;
    }
    const LeadOnly = LeaddropFormComponent;
    return (
      <div
        ref={(el) => {
          widgetRef.current = el;
        }}
        className={cn(
          "w-full min-w-0 max-w-full",
          isBlogPage && "box-border",
        )}
      >
        <LeadOnly hideTermsCheckbox />
      </div>
    );
  }

  return (
    <section
      ref={(el) => {
        widgetRef.current = el;
      }}
      className={cn(
        "relative w-full",
        // enablePadding
        // && "py-10 sm:py-10 md:py-[50px] lg:py-[60px] xl:py-10",
      )}
      style={{ backgroundColor }}
    >
      <div
        className={cn(
          "relative z-10 mx-auto w-full",
          "flex flex-col items-stretch gap-6 px-[24px] py-[40px]",
          // "sm:max-w-[1080px] sm:flex-row sm:items-start sm:gap-[40px] sm:py-[40px] sm:pl-[60px] sm:pr-[40px]",
          // "md:max-w-[1230px] md:pr-[60px]",
          // "lg:max-w-[1430px] lg:pl-[80px] lg:pr-[70px]",
          // "xl:max-w-[1750px] xl:gap-[110px] xl:px-[140px] xl:py-[80px]",
        )}
      >
        <div
          className={cn(
            "order-1 flex min-w-0 flex-1 flex-col gap-6",
            "w-full sm:max-w-[500px] md:max-w-[620px] lg:max-w-[732px] xl:max-w-[800px]",
          )}
        >
          {sectionData?.content ? (
            <article
              className={cn(
                "blog-with-lead-body rich-text-content max-w-none text-[15px] leading-[1.65] text-gray-800",
                "[&_a]:text-primary-main [&_a]:underline [&_strong]:font-semibold",
                "[&_h2]:mt-8 [&_h2]:mb-3 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-gray-900 [&_h2]:leading-snug",
                "[&_h3]:mt-6 [&_h3]:mb-2 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-gray-900",
              )}
              dangerouslySetInnerHTML={{ __html: sectionData.content }}
            />
          ) : null}
        </div>

        {leadFormAside}
      </div>
    </section>
  );
}
